import React, { createContext, useContext, useState, useEffect } from 'react';

const DataModeContext = createContext();

export const useDataMode = () => {
  const context = useContext(DataModeContext);
  if (!context) {
    throw new Error('useDataMode must be used within a DataModeProvider');
  }
  return context;
};

export const DataModeProvider = ({ children }) => {
  const [dataMode, setDataMode] = useState('demo'); // 'demo' oder 'real'
  const [isLoading, setIsLoading] = useState(false);

  // Lade gespeicherte Präferenz aus localStorage
  useEffect(() => {
    const savedMode = localStorage.getItem('clara-data-mode');
    if (savedMode && ['demo', 'real'].includes(savedMode)) {
      setDataMode(savedMode);
    }
  }, []);

  // Speichere Änderungen in localStorage
  useEffect(() => {
    localStorage.setItem('clara-data-mode', dataMode);
  }, [dataMode]);

  const toggleDataMode = async () => {
    setIsLoading(true);
    
    // Simuliere kurze Ladezeit für Datenumschaltung
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setDataMode(prev => prev === 'demo' ? 'real' : 'demo');
    setIsLoading(false);
  };

  const setMode = async (mode) => {
    if (!['demo', 'real'].includes(mode)) {
      console.error('Invalid data mode:', mode);
      return;
    }
    
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 300));
    setDataMode(mode);
    setIsLoading(false);
  };

  // Demo-Daten (hochgerechnete Werte)
  const demoData = {
    dashboard: {
      totalProperties: 12,
      totalTenants: 45,
      totalRent: 87500,
      occupancyRate: 94.2,
      netCashflow: 23750,
      profitMargin: 27.1,
      maintenanceTasks: 8,
      urgentTasks: 1
    },
    tenants: [
      {
        id: 'demo_1',
        name: 'Familie Schmidt',
        unit: 'Wohnung 3B',
        address: 'Waldhofstraße 76',
        rent: 1850,
        status: 'current',
        arrears: 0,
        lastPayment: '2024-06-25',
        contract: {
          start: '2023-01-15',
          end: '2025-01-14'
        }
      },
      {
        id: 'demo_2',
        name: 'Herr Weber',
        unit: 'Wohnung 4A',
        address: 'Waldhofstraße 76',
        rent: 1650,
        status: 'arrears',
        arrears: 3300,
        lastPayment: '2024-04-25',
        contract: {
          start: '2022-03-01',
          end: '2025-02-28'
        }
      },
      {
        id: 'demo_3',
        name: 'Firma ABC GmbH',
        unit: 'Büro 2C',
        address: 'Kaiserstraße 12',
        rent: 2200,
        status: 'current',
        arrears: 0,
        lastPayment: '2024-06-28',
        contract: {
          start: '2023-06-01',
          end: '2026-05-31'
        }
      },
      {
        id: 'demo_4',
        name: 'Familie Müller',
        unit: 'Wohnung 1A',
        address: 'Ludwigstraße 45',
        rent: 1750,
        status: 'current',
        arrears: 0,
        lastPayment: '2024-06-26',
        contract: {
          start: '2021-09-01',
          end: '2024-08-31'
        }
      },
      {
        id: 'demo_5',
        name: 'Herr Johnson',
        unit: 'Studio 5B',
        address: 'Ludwigstraße 45',
        rent: 1200,
        status: 'arrears',
        arrears: 4710,
        lastPayment: '2024-02-15',
        contract: {
          start: '2023-11-01',
          end: '2024-10-31'
        }
      }
    ],
    banking: {
      totalBalance: 45230,
      monthlyIncome: 87500,
      monthlyExpenses: 63750,
      netCashflow: 23750,
      profitMargin: 27.1,
      transactions: [
        {
          id: 'demo_tx_1',
          date: '2024-06-28',
          amount: 1850,
          type: 'income',
          category: 'rent',
          description: 'Miete Familie Schmidt - Wohnung 3B',
          tenant: 'Familie Schmidt'
        },
        {
          id: 'demo_tx_2',
          date: '2024-06-27',
          amount: -320,
          type: 'expense',
          category: 'maintenance',
          description: 'Heizungsreparatur Wohnung 3B',
          vendor: 'Heizungstechnik Schmidt'
        },
        {
          id: 'demo_tx_3',
          date: '2024-06-26',
          amount: 2200,
          type: 'income',
          category: 'rent',
          description: 'Miete Firma ABC GmbH - Büro 2C',
          tenant: 'Firma ABC GmbH'
        }
      ]
    }
  };

  // Real-Daten (Waldhofstraße 76 only)
  const realData = {
    dashboard: {
      totalProperties: 1,
      totalTenants: 4,
      totalRent: 4415,
      occupancyRate: 100,
      netCashflow: 1775,
      profitMargin: 76.18,
      maintenanceTasks: 4,
      urgentTasks: 1
    },
    tenants: [
      {
        id: 'real_1',
        name: 'Mieter A',
        unit: 'Café (Erdgeschoss)',
        address: 'Waldhofstraße 76',
        rent: 2050,
        status: 'current',
        arrears: 0,
        lastPayment: '2024-06-25',
        contract: {
          start: '2023-01-01',
          end: '2025-12-31'
        }
      },
      {
        id: 'real_2',
        name: 'Mieter B',
        unit: '1. OG Wohnung',
        address: 'Waldhofstraße 76',
        rent: 1300,
        status: 'current',
        arrears: 0,
        lastPayment: '2024-06-26',
        contract: {
          start: '2022-06-01',
          end: '2025-05-31'
        }
      },
      {
        id: 'real_3',
        name: 'Mieter C',
        unit: '3. OG Links',
        address: 'Waldhofstraße 76',
        rent: 445,
        status: 'current',
        arrears: 0,
        lastPayment: '2024-06-27',
        contract: {
          start: '2023-03-15',
          end: '2024-03-14'
        }
      },
      {
        id: 'real_4',
        name: 'Mieter D',
        unit: '1. OG Mitte',
        address: 'Waldhofstraße 76',
        rent: 620,
        status: 'current',
        arrears: 0,
        lastPayment: '2024-06-28',
        contract: {
          start: '2023-08-01',
          end: '2025-07-31'
        }
      }
    ],
    banking: {
      totalBalance: 2330,
      monthlyIncome: 4415,
      monthlyExpenses: 2640,
      netCashflow: 1775,
      profitMargin: 76.18,
      transactions: [
        {
          id: 'real_tx_1',
          date: '2024-06-28',
          amount: 2050,
          type: 'income',
          category: 'rent',
          description: 'Miete Mieter A - Café EG',
          tenant: 'Mieter A'
        },
        {
          id: 'real_tx_2',
          date: '2024-06-27',
          amount: 1300,
          type: 'income',
          category: 'rent',
          description: 'Miete Mieter B - 1. OG Wohnung',
          tenant: 'Mieter B'
        },
        {
          id: 'real_tx_3',
          date: '2024-06-26',
          amount: -320,
          type: 'expense',
          category: 'maintenance',
          description: 'Heizungsreparatur',
          vendor: 'Heizungstechnik Schmidt'
        }
      ]
    }
  };

  const getCurrentData = () => {
    return dataMode === 'demo' ? demoData : realData;
  };

  const getDataModeInfo = () => {
    if (dataMode === 'demo') {
      return {
        mode: 'demo',
        title: 'Demo-Modus',
        description: 'Hochgerechnete Werte für Präsentationszwecke',
        properties: '12 Objekte (simuliert)',
        tenants: '45 Mieter (extrapoliert)',
        dataSource: 'Basierend auf Waldhofstraße 76 × 19.8',
        warning: 'Nur für Demonstrations- und Testzwecke'
      };
    } else {
      return {
        mode: 'real',
        title: 'Real-Daten',
        description: 'Echte Daten der Waldhofstraße 76',
        properties: '1 Objekt (Waldhofstraße 76)',
        tenants: '4 Mieter (anonymisiert)',
        dataSource: 'CSV-Import vom 13.06.2025',
        warning: 'DSGVO-konform anonymisiert'
      };
    }
  };

  const value = {
    dataMode,
    isLoading,
    toggleDataMode,
    setMode,
    getCurrentData,
    getDataModeInfo,
    isDemoMode: dataMode === 'demo',
    isRealMode: dataMode === 'real'
  };

  return (
    <DataModeContext.Provider value={value}>
      {children}
    </DataModeContext.Provider>
  );
};

export default DataModeProvider;

